/**
 * TinyMCE 등 iframe body.innerHTML 로 넣기 전에 사용.
 * - 미완성 태그 "<p><aa</p>" 파서 복구 완화
 * - 알 수 없는 빈 태그 "<p><aa></p>" 는 TinyMCE 가 제거·무시해 안 보이는 경우가 있어, 허용 목록에 없으면 텍스트로 이스케이프
 *
 * 하이픈 포함 이름(x-foo)은 HTML 커스텀 엘리먼트로 보고 통과.
 * 주의: 속성값에 따옴표 없이 `>`가 들어가는 등 비정형 마크업은 완벽히 다루지 못할 수 있다.
 */

/**
 * TinyMCE 기본 schema 타입 `html5` 와 맞춤 (tinymce.js → getElementSetsAsStrings + makeSchema 본문 관련 태그).
 * 툴바에 없어도 붙여넣기·HTML 붙여넣기로 들어올 수 있는 태그를 포함한다.
 *
 * - 스키마에서 제거되는 `script` 는 목록에서 제외(실질적으로 붙여넣기 시에도 비허용에 가깝다).
 * - `extended_valid_elements` 등으로 스키마를 넓힌 화면은 Set 보강 또는 `normalizeHtmlBeforeSet` 해제.
 *
 * 출처 요약 (tinymce 7.x 기준):
 * - blockContent + phrasingContent (html5 + html4 호환 묶음)
 * - 표·폼 등 flow 에만 나오는 자식 태그: li, dt, dd, caption, col, …
 * - svg 루트는 phrasing 에 포함, 하위는 별도 나열
 */
const TINYMCE_HTML5_BLOCK =
  'address blockquote div dl fieldset form h1 h2 h3 h4 h5 h6 hr menu ol p pre table ul ' +
  'article aside details dialog figure main header footer hgroup section nav ' +
  'a ins del canvas map center dir isindex noframes';

const TINYMCE_HTML5_PHRASING =
  'a abbr b bdo br button cite code del dfn em embed i iframe img input ins kbd ' +
  'label map noscript object q s samp select small span strong sub sup textarea u var ' +
  'audio canvas command data datalist mark meter output picture progress time wbr video ruby bdi keygen svg ' +
  'acronym applet basefont big font strike tt';

/** makeSchema 에서 flow 문자열만으로는 빠지는 구조 태그 */
const TINYMCE_STRUCTURE_EXTRA =
  'li dt dd caption col colgroup tbody thead tfoot tr th td param area legend option optgroup source track ' +
  'figcaption summary rp rt style';

/** SVG 붙여넣기 시 자주 쓰이는 하위 태그 (svg 는 phrasing 에 포함됨) */
const TINYMCE_SVG_COMMON =
  'path circle ellipse line polygon polyline rect g defs use text tspan image clipPath mask ' +
  'linearGradient radialGradient stop title desc pattern foreignObject';

const DISALLOWED_IN_NORMALIZER = new Set(['#text', '#comment', 'script']);

function buildAllowedHtmlTagSet(): Set<string> {
  const raw = [TINYMCE_HTML5_BLOCK, TINYMCE_HTML5_PHRASING, TINYMCE_STRUCTURE_EXTRA, TINYMCE_SVG_COMMON].join(' ');
  const set = new Set<string>();
  for (const t of raw.split(/\s+/)) {
    if (!t) continue;
    const lower = t.toLowerCase();
    if (DISALLOWED_IN_NORMALIZER.has(lower)) continue;
    set.add(lower);
  }
  return set;
}

const ALLOWED_HTML_TAGS = buildAllowedHtmlTagSet();

function isAllowedTagName(name: string): boolean {
  const n = name.toLowerCase();
  if (ALLOWED_HTML_TAGS.has(n)) return true;
  if (n.includes('-')) return true;
  if (n.includes(':')) return true;
  return false;
}

function escapeInvalidOpeningAngles(html: string): string {
  let out = '';
  let i = 0;
  while (i < html.length) {
    const ch = html[i];
    if (ch !== '<') {
      out += ch;
      i += 1;
      continue;
    }

    const rest = html.slice(i);

    if (rest.startsWith('<!--')) {
      const end = html.indexOf('-->', i + 4);
      if (end === -1) {
        out += '&lt;';
        i += 1;
        continue;
      }
      out += html.slice(i, end + 3);
      i = end + 3;
      continue;
    }

    if (rest.startsWith('<![CDATA[')) {
      const end = html.indexOf(']]>', i + 9);
      if (end === -1) {
        out += '&lt;';
        i += 1;
        continue;
      }
      out += html.slice(i, end + 3);
      i = end + 3;
      continue;
    }

    let m = /^<\/([a-zA-Z][\w:-]*)\s*>/.exec(rest);
    if (m) {
      if (isAllowedTagName(m[1])) {
        out += m[0];
        i += m[0].length;
      } else {
        out += `&lt;/${m[1]}&gt;`;
        i += m[0].length;
      }
      continue;
    }

    m = /^<([a-zA-Z][\w:-]*)(?:\s|\n)[^>]*>/.exec(rest);
    if (m) {
      if (isAllowedTagName(m[1])) {
        out += m[0];
        i += m[0].length;
      } else {
        // 허용되지 않은 태그(예: 본문에 구분자로 쓴 "<Note>", "<Case1>")는
        // 여는 '<' 만 이스케이프하면 나머지(태그명 + 닫는 '>')가 그대로 원문에 남아
        // "&lt;Note>" 처럼 짝이 안 맞는 상태로 저장된다. 매칭된 전체를 통째로 이스케이프.
        out += `&lt;${m[0].slice(1, -1)}&gt;`;
        i += m[0].length;
      }
      continue;
    }

    m = /^<([a-zA-Z][\w:-]*)\s*\/>/.exec(rest);
    if (m) {
      if (isAllowedTagName(m[1])) {
        out += m[0];
        i += m[0].length;
      } else {
        out += `&lt;${m[0].slice(1, -1)}&gt;`;
        i += m[0].length;
      }
      continue;
    }

    m = /^<([a-zA-Z][\w:-]*)>/.exec(rest);
    if (m) {
      if (isAllowedTagName(m[1])) {
        out += m[0];
        i += m[0].length;
      } else {
        out += `&lt;${m[0].slice(1, -1)}&gt;`;
        i += m[0].length;
      }
      continue;
    }

    out += '&lt;';
    i += 1;
  }
  return out;
}

export function normalizeHtmlBeforeEditor(html: string | null | undefined): string {
  if (html == null || html === '') return '';
  return escapeInvalidOpeningAngles(String(html));
}
